import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  ControlCode,
  ControlMonitorEndpointPolicy,
  ControlMonitorInboxPolicy,
  ControlMonitorUserPolicy,
  formatLocalDate,
  getControlCategory,
  getControlCategoryName,
  getControlName,
  greaterThanToday,
  isEmailControl,
  isIdentityProviderControl,
  parseDate,
  queries,
  service,
  TimeFormats,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  CloseIcon,
  ControlCategoryIcon,
  ControlIcon,
  DateInputFormField,
  ExceptionIcon,
  Form,
  FormField,
  notifyError,
  notifySuccess,
  Overlay,
  OverlayLoading,
  PlatformIcon,
  PlusIcon,
  TrashcanIcon,
  useConfirmSubmit,
} from "@theprelude/ds";
import isEqual from "lodash/isEqual";
import { Suspense, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ResponsiveContainer } from "@/components/responsive-container";
import {
  policyArrowColumn,
  policySettingNameColumn,
  policySettingRecommendationColumn,
  policySettingValueColumn,
} from "@/components/table/columns";
import { useTable } from "@/components/table/hooks";
import { VirtualTable } from "@/components/table/virtual-table";
import { useOverlay } from "@/hooks/use-overlay";
import { controlInstanceSchema } from "@/lib/validators";
import { useTopLevelFiltersQuery } from "@/queries/control-monitor";
import { calculateExpirationDate } from "@/queries/exceptions";
import { OverlayErrorFallback } from "../../default-overlay";
import { Breadcrumbs } from "../breadcrumbs";
import { DeleteExceptionDialog } from "../delete-exception-modal";
import { SaveExceptionDialog } from "../save-exception-modal";
import { PolicySetting } from "./policies-overlay";

export const addPolicyExceptionSchema = z.tuple([
  z.literal("add-policy-exception"),
  controlInstanceSchema.extend({ policyId: z.string() }),
]);

export type AddPolicyExceptionSchema = z.infer<
  typeof addPolicyExceptionSchema
>[1];

export function AddPolicyExceptionOverlay({
  instanceId,
  control,
  policyId,
}: AddPolicyExceptionSchema) {
  const overlay = useOverlay();

  return (
    <Overlay
      title={
        <Suspense fallback={<TitleLoading />}>
          <Title
            instanceId={instanceId}
            control={control}
            policyId={policyId}
          />
        </Suspense>
      }
      position="right"
      defaultSize={60}
      hideOverlay={overlay.close}
    >
      <ErrorBoundary FallbackComponent={OverlayErrorFallback}>
        <Suspense fallback={<OverlayLoading />}>
          <AddPolicyException
            instanceId={instanceId}
            control={control}
            policyId={policyId}
          />
        </Suspense>
      </ErrorBoundary>
    </Overlay>
  );
}

const Title = ({ instanceId, control, policyId }: AddPolicyExceptionSchema) => {
  const { data: exception } = usePolicyExceptionQuery({
    instanceId,
    control,
    policyId,
  });

  return (
    <div className="flex items-center gap-2">
      <ExceptionIcon />
      {exception ? "Edit Exception" : "Add Exception"}
    </div>
  );
};

const TitleLoading = () => (
  <div className="flex items-center gap-2">
    <ExceptionIcon />
    <div className="h-3 w-96 animate-pulse rounded-md bg-secondary-foreground" />
  </div>
);

const AddPolicyException = ({
  instanceId,
  control,
  policyId,
}: AddPolicyExceptionSchema) => {
  const router = useRouter();

  const { data: policy } = usePolicyQuery({ instanceId, control, policyId });

  const category = getControlCategory(control);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-col gap-2">
        <Breadcrumbs
          path={[
            {
              label: getControlCategoryName(category),
              icon: <ControlCategoryIcon category={category} />,
            },
            {
              label: getControlName(control),
              icon: <ControlIcon control={control} />,
            },
          ]}
        />
        {policy && (
          <div className="flex items-center gap-2 text-xl">
            {hasPlatform(policy) && <PlatformIcon platform={policy.platform} />}
            {policy.name}
          </div>
        )}
      </div>
      <PolicyExceptionForm
        instanceId={instanceId}
        control={control}
        policyId={policyId}
        onCancel={router.history.back}
      />
    </div>
  );
};

const hasPlatform = (
  policy:
    | ControlMonitorEndpointPolicy
    | ControlMonitorInboxPolicy
    | ControlMonitorUserPolicy,
): policy is ControlMonitorEndpointPolicy => Object.hasOwn(policy, "platform");

const without = <T,>(arr: T[], value: T) => {
  const result = [...arr];
  for (let i = 0; i < result.length; ) {
    if (isEqual(result[i], value)) {
      result.splice(i, 1);
    } else {
      i += 1;
    }
  }
  return result;
};

type KeyedPolicySetting = PolicySetting & { key: string };

const formSchema = z.object({
  settings: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .min(1, "You must select at least one configuration item"),
  expires: z.string().refine((date) => {
    return !date || !greaterThanToday(parseDate(date), 0);
  }, "The expiration date must be in the future"),
});

type FormSchema = z.infer<typeof formSchema>;

const useSaveExceptionMutation = ({
  control,
  instanceId,
  policyId,
}: {
  control: ControlCode;
  instanceId: string;
  policyId: string;
}) => {
  const authConfig = useAuthConfig();
  const overlay = useOverlay();
  const router = useRouter();

  return useMutation({
    mutationFn: async (data: FormSchema) => {
      const expires = data.expires
        ? calculateExpirationDate(data.expires)
        : undefined;
      return service(authConfig).scm.updatePolicyException({
        control,
        instanceId,
        policyId,
        settingNames: data.settings.map((s) => s.name),
        expires,
      });
    },
    onSuccess: () => {
      notifySuccess({
        message: (
          <div className="flex items-center gap-16">
            <span>Exception has been updated.</span>
            <Button
              className="whitespace-nowrap border border-stroke-input bg-transparent text-white"
              onClick={() => overlay.open("exceptions", { filter: "all" })}
            >
              View all exceptions
            </Button>
          </div>
        ),
        duration: Number.POSITIVE_INFINITY,
        dismissible: true,
      });
      router.history.back();
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to update this exception.",
        error,
        dismissible: true,
      });
    },
  });
};

const useDeleteExceptionMutation = ({
  control,
  instanceId,
  policyId,
}: {
  control: ControlCode;
  instanceId: string;
  policyId: string;
}) => {
  const authConfig = useAuthConfig();
  const router = useRouter();

  return useMutation({
    mutationFn: async () => {
      return service(authConfig).scm.updatePolicyException({
        control,
        instanceId,
        policyId,
        settingNames: [],
        expires: undefined,
      });
    },
    onSuccess: () => {
      notifySuccess({
        message: "Exception has been deleted.",
        duration: Number.POSITIVE_INFINITY,
        dismissible: true,
      });
      router.history.back();
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to delete this exception.",
        error,
        dismissible: true,
      });
    },
  });
};

type PolicyExceptionFormProps = AddPolicyExceptionSchema & {
  onCancel: () => void;
};

const PolicyExceptionForm = ({
  instanceId,
  control,
  policyId,
  onCancel,
}: PolicyExceptionFormProps) => {
  const saveConfirmation = useConfirmSubmit<FormSchema>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { data: exception } = usePolicyExceptionQuery({
    instanceId,
    control,
    policyId,
  });
  const { data: policy } = usePolicyQuery({ instanceId, control, policyId });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      settings: (exception?.excepted_settings ?? []).map((s) => ({
        id: s.setting_id,
        name: s.setting_name,
      })),
      expires: exception?.expires
        ? formatLocalDate(parseDate(exception.expires), TimeFormats.DATE_SHORT)
        : "",
    },
  });

  const { register, setValue, watch } = form;

  const selectedSettings = watch("settings");

  const { mutate: createException, isPending: isSaving } =
    useSaveExceptionMutation({ control, instanceId, policyId });

  const { mutate: deleteException, isPending: isDeleting } =
    useDeleteExceptionMutation({ control, instanceId, policyId });

  const onSubmit = () => {
    if (saveConfirmation.isOpen) {
      saveConfirmation.close();
      createException(saveConfirmation.data);
    }
  };

  const onDelete = () => {
    setShowDeleteConfirmation(false);
    deleteException();
  };

  const isPending = isSaving || isDeleting;

  const settings = useMemo(() => {
    if (!policy) {
      return [];
    }
    return policy.settings.map(
      (setting) =>
        ({
          ...setting,
          key: `${setting.id}-${setting.name}`,
          policyId: policy.id,
          platform: "platform" in policy ? policy.platform : undefined,
          control,
        }) satisfies KeyedPolicySetting,
    );
  }, [control, policy]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<KeyedPolicySetting>();
    const columns = [
      columnHelper.accessor("name", policySettingNameColumn),
      columnHelper.accessor("value", policySettingValueColumn),
      columnHelper.accessor("id", policyArrowColumn),
      columnHelper.accessor(
        "recommendation",
        policySettingRecommendationColumn,
      ),
      columnHelper.display({
        id: "actions",
        enableHiding: false,
        cell: (info) => (
          <div className="flex w-full justify-end">
            {selectedSettings.some((s) => s.name === info.row.original.name) ? (
              <Button
                type="button"
                intent="pill"
                icon={<CloseIcon />}
                size="tiny"
                disabled={isPending}
                onClick={() =>
                  setValue(
                    "settings",
                    without(selectedSettings, {
                      id: info.row.original.id,
                      name: info.row.original.name,
                    }),
                  )
                }
                className="whitespace-nowrap"
              >
                Remove Exception
              </Button>
            ) : (
              <Button
                type="button"
                intent="pill"
                icon={<PlusIcon className="text-secondary-foreground" />}
                size="tiny"
                disabled={isPending}
                onClick={() =>
                  setValue("settings", [
                    ...selectedSettings,
                    { id: info.row.original.id, name: info.row.original.name },
                  ])
                }
                className="whitespace-nowrap"
              >
                Add Exception
              </Button>
            )}
          </div>
        ),
        meta: {
          label: " ",
          widths: ["135px", "1fr"],
          canExport: false,
        },
      }),
    ];
    return columns;
  }, [isPending, selectedSettings, setValue]);

  const table = useTable({
    name: "add-policy-exception",
    data: settings,
    columns,
  });

  if (policy === undefined) {
    throw new Error("Policy not found.");
  }

  return (
    <Form
      {...form}
      onSubmitValid={saveConfirmation.open}
      className="flex h-full flex-col gap-4"
    >
      <FormField name="settings" className="flex-1 overflow-hidden">
        <ResponsiveContainer className="h-full">
          {({ height }) => <VirtualTable table={table} height={height} />}
        </ResponsiveContainer>
      </FormField>
      <DateInputFormField
        {...register("expires")}
        label="Exception expiration date"
        disabled={isPending}
      />
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            type="button"
            intent="secondary"
            disabled={isPending}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            intent="primary"
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Button>
        </div>
        {exception && (
          <Button
            type="button"
            intent="negative-inverted"
            icon={<TrashcanIcon />}
            onClick={() => setShowDeleteConfirmation(true)}
          >
            Delete this exception
          </Button>
        )}
      </div>
      <SaveExceptionDialog
        open={saveConfirmation.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            saveConfirmation.close();
          }
        }}
        onConfirm={onSubmit}
      />
      <DeleteExceptionDialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        onConfirm={onDelete}
      />
    </Form>
  );
};

const usePolicyExceptionQuery = ({
  instanceId,
  control,
  policyId,
}: AddPolicyExceptionSchema) => {
  const authConfig = useAuthConfig();
  return useSuspenseQuery({
    ...queries.scm.policyExceptions(authConfig),
    select: (exceptions) =>
      exceptions.find(
        (e) =>
          e.instance_id === instanceId &&
          e.control === control &&
          e.policy_id === policyId,
      ),
  });
};

const usePolicyQuery = ({
  instanceId,
  control,
  policyId,
}: AddPolicyExceptionSchema) => {
  const category = isEmailControl(control)
    ? "inboxes"
    : isIdentityProviderControl(control)
      ? "users"
      : "endpoints";

  const authConfig = useAuthConfig();
  const topLevelFiltersQuery = useTopLevelFiltersQuery(category);
  const router = useRouterState();

  const threatId = router.location.search.threat_id;
  const { data: threat } = useSuspenseQuery({
    ...queries.scm.threats(authConfig),
    select: (threats) => threats.find((t) => t.id === threatId),
  });
  const techniques = threat?.techniques ?? [];

  return useSuspenseQuery({
    ...queries.scm.policies(authConfig, control, instanceId, {
      techniques,
      query: topLevelFiltersQuery,
    }),
    select: (data) =>
      data.endpoint_evaluation?.policies.find((p) => p.id === policyId) ??
      data.inbox_evaluation?.policies.find((p) => p.id === policyId) ??
      data.user_evaluation?.policies.find((p) => p.id === policyId),
  });
};
